/** The numbers beside each vendor on the Vendors table (`GET /sources/vendors/summary`), and the
 * organisation tree (`GET /org/tree`) the Facilities tabs are drawn from.
 *
 * A vendor is the company's own note of a party that bills it; a supplier is another company's
 * workspace (ADR-0040). These reads never join them. Thrown on failure, never defaulted to empty: an
 * empty vendor table would say nothing is billed.
 */

import { skipToken, useQuery } from "@tanstack/react-query";

import { api } from "./client";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";

export type VendorSummary = components["schemas"]["VendorSummaryOut"];
export type VendorSummaryRow = components["schemas"]["VendorSummaryRowOut"];
export type OrgTree = components["schemas"]["OrgTreeOut"];
export type TreeEntity = components["schemas"]["TreeEntityOut"];
export type TreeFacility = components["schemas"]["TreeFacilityOut"];
export type TreeEdge = components["schemas"]["TreeEdgeOut"];

export function useVendorSummary(
  periodStart: string | undefined,
  periodEnd: string | undefined,
  method: "location" | "market",
) {
  return useQuery({
    queryKey: ["vendors", "summary", periodStart ?? "none", periodEnd ?? "none", method] as const,
    queryFn:
      periodStart && periodEnd
        ? async (): Promise<VendorSummary> => {
            const { data, error, response } = await api.GET("/api/v1/sources/vendors/summary", {
              params: {
                query: { period_start: periodStart, period_end: periodEnd, scope2_method: method },
              },
            });
            if (error || !data) throw refusalFrom(response, error, "The vendors could not be read.");
            return data;
          }
        : skipToken,
  });
}

export function useOrgTree(
  periodStart: string | undefined,
  periodEnd: string | undefined,
  method: "location" | "market",
) {
  return useQuery({
    queryKey: ["org-tree", periodStart ?? "none", periodEnd ?? "none", method] as const,
    queryFn:
      periodStart && periodEnd
        ? async (): Promise<OrgTree> => {
            const { data, error, response } = await api.GET("/api/v1/org/tree", {
              params: {
                query: { period_start: periodStart, period_end: periodEnd, scope2_method: method },
              },
            });
            if (error || !data) {
              throw refusalFrom(response, error, "The structure of the organisation could not be read.");
            }
            return data;
          }
        : skipToken,
  });
}
